import {gql} from '@apollo/client'
import {orderFragment} from '../components/Orders/order-queries'

export const linesFragment = gql`
  fragment LineFields on CheckoutLine {
    id,
    variant {
      id,
      name,
      sku,
      pricing {
        price {
          net {
            amount
          }
          gross {
            amount
          }
        }
      }
      product {
        id,
        name,
        slug,
        thumbnail {
          url
          alt
        }
        thumbnail2x: thumbnail(size: 510) {
          url
        }
      }
    },
    quantity,
    totalPrice {
      net {
        amount
      }
      gross {
        amount
      }
    }
  }
`

export const checkoutFragment = gql`
  ${linesFragment}
  fragment CheckoutFields on Checkout {
    quantity,
    id,
    token,
    shippingAddress {
      id
      firstName
      streetAddress1
    },
    billingAddress {
      id
      firstName
      streetAddress1
    },
    availableShippingMethods {
      id
      name
      price {
        amount
      }
    },
    availablePaymentGateways {
      id
      name
    },
    lines {
      ...LineFields
    }
    subtotalPrice {
      net {
        amount
      }
      gross {
        amount
      }
    }
    totalPrice {
      net {
        amount
      }
      gross {
        amount
      }
    }
    metadata {
      key
      value
    }
  }
`

export const checkoutQuery = gql`
  ${checkoutFragment}
  query Checkout($token: UUID!) {
    checkout(token: $token) {
      ...CheckoutFields
    }
  }
`

export const checkoutCreateMutation = gql`
  ${checkoutFragment}
  mutation CheckoutCreate($email: String) {
    checkoutCreate(
      input: {
        lines: [],
        email: $email,
      }
    ) {
      created
      checkoutErrors {
        field
        message
        code
      }
      checkout {
        ...CheckoutFields
      }
    }
  }
`

export const checkoutLinesAddMutation = gql`
  ${checkoutFragment}
  mutation CheckoutLinesAdd($checkoutId: ID!, $lines: [CheckoutLineInput]!) {
    checkoutLinesAdd(
      checkoutId: $checkoutId,
      lines: $lines,
    ) {
      checkout {
        ...CheckoutFields
      }
      checkoutErrors {
        field
        message
        code
      }
    }
  }
`

export const checkoutLinesUpdateMutation = gql`
  ${checkoutFragment}
  mutation CheckoutLinesUpdate($checkoutId: ID!, $lines: [CheckoutLineInput]!) {
    checkoutLinesUpdate(
      checkoutId: $checkoutId,
      lines: $lines,
    ) {
      checkout {
        ...CheckoutFields
      }
      checkoutErrors {
        field
        message
        code
      }
    }
  }
`

export const myCheckoutQuery = gql`
  ${checkoutFragment}
  query MyCheckout {
    me {
      checkout {
        ...CheckoutFields
      }
    }
  }
`

export const checkoutShippingAddressUpdateMutation = gql`
  ${checkoutFragment}
  mutation CheckoutShippingAddressUpdate($checkoutId: ID!, $shippingAddress: AddressInput!) {
    checkoutShippingAddressUpdate(
      checkoutId: $checkoutId,
      shippingAddress: $shippingAddress,
    ) {
      checkout {
        ...CheckoutFields
      }
      checkoutErrors {
        field
        message
        code
      }
    }
  }
`

export const checkoutBillingAddressUpdateMutation = gql`
  ${checkoutFragment}
  mutation CheckoutBillingAddressUpdate($checkoutId: ID!, $billingAddress: AddressInput!) {
    checkoutBillingAddressUpdate(
      checkoutId: $checkoutId,
      billingAddress: $billingAddress,
    ) {
      checkout {
        ...CheckoutFields
      }
      checkoutErrors {
        field
        message
        code
      }
    }
  }
`

export const checkoutShippingMethodUpdateMutation = gql`
  ${checkoutFragment}
  mutation CheckoutShippingMethodUpdate($checkoutId: ID!, $shippingMethodId: ID!) {
    checkoutShippingMethodUpdate(
      checkoutId: $checkoutId,
      shippingMethodId: $shippingMethodId,
    ) {
      checkout {
        ...CheckoutFields
      }
      checkoutErrors {
        field
        message
        code
      }
    }
  }
`

export const checkoutPaymentCreateMutation = gql`
  ${checkoutFragment}
  mutation CheckoutPaymentCreate($checkoutId: ID!, $input: PaymentInput!) {
    checkoutPaymentCreate(
      checkoutId: $checkoutId,
      input: $input,
    ) {
      payment {
        id
        gateway
        token
      }
      checkout {
        ...CheckoutFields
      }
      paymentErrors {
        field
        message
        code
      }
    }
  }
`

export const checkoutCompleteMutation = gql`
  ${orderFragment}
  mutation CheckoutComplete($checkoutId: ID!, $redirectUrl: String) {
    checkoutComplete(
      checkoutId: $checkoutId,
      redirectUrl: $redirectUrl,
    ) {
      order {
        ...OrderFields
      }
      confirmationNeeded
      confirmationData
      checkoutErrors {
        field
        message
        code
      }
    }
  }
`

export const checkoutUpdateMetadataMutation = gql`
  mutation CheckoutUpdateMetadata($id: ID!, $input: MetaInput!) {
    checkoutUpdateMetadata(id: $id, input: $input) {
      checkoutErrors {
        field
        message
        code
      }
    }
  }
`
